import { useEffect, useMemo } from 'react';

import { create } from 'zustand';

import { UNIT_ID_LABEL_KEY } from 'src/common/consts/platformConfigConsts';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { Logger } from 'src/common/services/Logger';
import { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { processPlatformConfiguration } from '../helpers/ConfigurationProcessingHelpers';
import { sanitizeConfigurationLabel } from '../helpers/DataSanitizationHelpers';
import { BusinessUnit } from '../types/DashboardTypes';

const toBusinessUnits = (labels: ConfigurationLabel[]): BusinessUnit[] => {
	const businessUnitLabel = labels.find((label) => label.label === UNIT_ID_LABEL_KEY);

	if (!businessUnitLabel) {
		Logger.warn('No unitId label found in configuration');
		return [];
	}

	return sanitizeConfigurationLabel(businessUnitLabel).values.map((value) => ({
		id: value,
		name: value
	}));
};

interface IDashboardConfigStore {
	businessUnits: BusinessUnit[];
	isLoading: boolean;
	error: string | null;

	// Actions
	setBusinessUnits: (units: BusinessUnit[]) => void;
	clearBusinessUnits: () => void;

	// Selectors
	getBusinessUnitById: (id: string) => BusinessUnit | null;
	getFirstBusinessUnit: () => BusinessUnit | null;
}

export const useDashboardConfigStore = create<IDashboardConfigStore>((set, get) => ({
	businessUnits: [],
	isLoading: false,
	error: null,

	setBusinessUnits: (units: BusinessUnit[]) =>
		set({
			businessUnits: units,
			isLoading: false,
			error: null
		}),

	clearBusinessUnits: () =>
		set({
			businessUnits: [],
			isLoading: false,
			error: null
		}),

	getBusinessUnitById: (id: string) => {
		const { businessUnits } = get();
		return businessUnits.find((unit) => unit.id === id) || null;
	},

	getFirstBusinessUnit: () => {
		const { businessUnits } = get();
		return businessUnits.length > 0 ? businessUnits[0] : null;
	}
}));

export const useBusinessUnitsFromConfig = (): {
	businessUnits: BusinessUnit[];
	isLoading: boolean;
	error: string | null;
} => {
	const { configuration } = useConfiguration();
	const { businessUnits, setBusinessUnits, error } = useDashboardConfigStore();

	// Extract business units from configuration when it's available
	useEffect(() => {
		if (!configuration) {
			return;
		}

		if (businessUnits.length > 0) {
			return;
		}

		if (!configuration.PLATFORM) {
			Logger.warn('No PLATFORM configuration found');
			setBusinessUnits([]);
			return;
		}

		try {
			const { coreLabels } = processPlatformConfiguration(configuration.PLATFORM);
			setBusinessUnits(toBusinessUnits(coreLabels));
		} catch (error) {
			Logger.error('Failed to extract business units from configuration:', error);
			setBusinessUnits([]);
		}
	}, [configuration, businessUnits.length, setBusinessUnits]);

	const actualIsLoading = !configuration && businessUnits.length === 0;

	return {
		businessUnits,
		isLoading: actualIsLoading,
		error
	};
};

export const useMobileBusinessUnitsFromConfig = (): {
	businessUnits: BusinessUnit[];
	isLoading: boolean;
	error: string | null;
} => {
	const { configuration } = useConfiguration();

	const businessUnits = useMemo((): BusinessUnit[] => {
		if (!configuration?.PLATFORM) {
			return [];
		}

		try {
			const { mobileCoreLabels } = processPlatformConfiguration(configuration.PLATFORM);
			return toBusinessUnits(mobileCoreLabels);
		} catch (err) {
			Logger.error('Failed to extract mobile business units from configuration:', err);
			return [];
		}
	}, [configuration]);

	return {
		businessUnits,
		isLoading: !configuration,
		error: null
	};
};
